/**
 * MoveStageDialog — the gated-move forms (PR4a). When a card is dropped into
 * Client Setup or Assigned, the board opens this to collect the required
 * inputs, then calls onConfirm with the move-stage payload.
 *
 *   mode 'client-setup' → Add client's boxes (name, preferred name, service,
 *                         pronouns, rate set, the duplicate warning) plus the
 *                         CareTree id; creates the client. The service is
 *                         required, with no pre-select.
 *   mode 'assign'       → a care-manager pick (optional) + a unit multi-select.
 *                         STOP client-side iff neither chosen (mirrors the
 *                         server's 409); units by id, never name.
 */

import { useState, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { useAssignOptions } from '@/lib/workspace';
import type { SuggestedCareManagerRow } from '@/components/capacity/SuggestedCareManagersTable';
import { SuggestedCareManagers } from '@/components/workspace/SuggestedCareManagers';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { TextAction } from '@/components/workspace/TextAction';
import { CmPhoto } from './CmPhoto';
import type { MoveStageInput } from '@/lib/api';
import { CLIENT_PRONOUN_VALUES } from '@/lib/noteVocabulary';
import { SelectField } from '@/components/primitives/SelectField';
import { pageReadText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { Link } from 'react-router-dom';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useClientsList, type ClientEngagementValue } from '@/lib/clientsApi';
import { canManageClientRates } from '@/lib/capabilities';
import { readRateSets } from '@/lib/rateSets';
import { localDayOf, proseDate } from '@/lib/proseDate';
import { useLinkCandidate } from '@/lib/linkCandidate';
import { duplicateClient } from '@/lib/addClient';
import { pronounForms } from '@/lib/pronounForms';
import type { ClientPronouns } from '@/lib/clientRecord';
import { ENGAGEMENT_LABELS } from '@/components/clientRecord/VitalsBand';

export type MoveStageMode = 'client-setup' | 'assign';

export interface MoveStageDialogProps {
  mode: MoveStageMode;
  toStage: string;
  /** Pre-fill (e.g. an intake's prospect name split). */
  defaultFirstName?: string;
  defaultLastName?: string;
  /** Slice 3b: the work_item's geographic unit — default the CM list to its CMs. */
  unitId?: string | null;
  /** The intake card's ZIP (work_items.prospect_zip). Client Setup compares
   *  it with a linked client's home ZIP. */
  prospectZip?: string | null;
  /** Client Setup: the card's id, for "Use this client"'s candidate read. */
  workItemId?: string | null;
  /** The intake card's client (work_items.client_id). With a client or a
   *  ZIP, the dialog asks the assignment-suggestions route (the client's
   *  home address decides, the ZIP stands in when it cannot) and shows the
   *  covering unit and the care managers in two blocks above the picker.
   *  With neither it asks nothing. */
  clientId?: string | null;
  /** A care manager picked from the checklist's Suggested block: the picker
   *  opens with them selected (and widened past the card's unit so the
   *  pick is visible). Nothing is assigned until Assign is pressed. */
  initialRosterId?: string | null;
  /** LP-3: offer the SPICE therapist picker (the Ready for Assignment move;
   *  an Assigned case's in-place reassign carries the care manager only). */
  offerSpice?: boolean;
  /** Opened from "Assign SPICE therapist": the dialog is titled for her. */
  spiceFirst?: boolean;
  onConfirm: (payload: Omit<MoveStageInput, 'to_stage'>) => void;
  onCancel: () => void;
  submitting?: boolean;
}

export function MoveStageDialog(props: MoveStageDialogProps): ReactNode {
  if (props.mode === 'client-setup') return <ClientSetupForm {...props} />;
  return <AssignForm {...props} />;
}

function ClientSetupForm({ defaultFirstName, defaultLastName, onConfirm, onCancel, submitting, workItemId, prospectZip }: MoveStageDialogProps): ReactNode {
  // Add client's boxes (Clients.tsx AddClientDialog, the addClient.ts
  // rules), for the client this card becomes, plus the CareTree Patient
  // Id the move already took. No lead pickers: Assigned sets the lead on
  // this path.
  const { tenantSlug, whoami } = useTenant();
  const roster = useClientsList(tenantSlug, { includeArchived: true });
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const rateSets = readRateSets(tenantConfig?.config);
  const offerRateSets = canManageClientRates(whoami) && rateSets.length > 0;
  const [firstName, setFirstName] = useState(defaultFirstName ?? '');
  const [lastName, setLastName] = useState(defaultLastName ?? '');
  const [preferred, setPreferred] = useState('');
  // No pre-select: the card's "service requested" is the practice's own
  // wording, so a person chooses (an unchosen service is never assumed).
  const [engagement, setEngagement] = useState<ClientEngagementValue | ''>('');
  const [serviceGap, setServiceGap] = useState(false);
  const [caretreeId, setCaretreeId] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [rateSetId, setRateSetId] = useState('');
  const duplicate = duplicateClient(roster.clients, firstName, lastName);
  // "Use this client": the confirm step for linking this card to the
  // matched client instead of creating one. Back returns to the form with
  // everything typed kept (the state lives here).
  const [linking, setLinking] = useState<string | null>(null);

  if (linking !== null && workItemId) {
    return (
      <Dialog title="Use an existing client" testId="move-stage-dialog" onClose={onCancel}>
        <LinkConfirmStep
          workItemId={workItemId}
          clientId={linking}
          cardZip={prospectZip ?? null}
          typed={{ engagement, pronouns, preferred_name: preferred.trim(), caretree_external_id: caretreeId.trim() }}
          submitting={submitting}
          onBack={() => setLinking(null)}
          onLink={() => onConfirm({ client_setup: { link_client_id: linking } })}
        />
      </Dialog>
    );
  }

  return (
    <Dialog title="Client Setup" testId="move-stage-dialog" onClose={onCancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (engagement === '') {
            setServiceGap(true);
            return;
          }
          onConfirm({
            client_setup: {
              first_name: firstName.trim() || null,
              last_name: lastName.trim() || null,
              preferred_name: preferred.trim() || null,
              engagement,
              caretree_external_id: caretreeId.trim() || null,
              // 0128 — the founder's intake capture point; optional.
              pronouns: pronouns || null,
              ...(offerRateSets && rateSetId !== ''
                ? { rate_set_id: rateSetId, rate_effective_from: localDayOf(new Date().toISOString()) }
                : {}),
            },
          });
        }}
      >
        <label className="block text-sm text-ink mb-2">
          First name
          <TextInput nameCase="person" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="cs-first-name"
            className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
        </label>
        <label className="block text-sm text-ink mb-2">
          Last name
          <TextInput nameCase="person" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="cs-last-name"
            className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
        </label>
        {duplicate !== null && (
          <p role="status" data-testid="cs-duplicate" className="mb-2 text-sm text-ink">
            {`There is already a client named ${duplicate.display_name} (${duplicate.is_active ? 'active' : 'archived'}). `}
            <Link
              to={`/t/${tenantSlug}/clients/${duplicate.id}`}
              className="font-semibold text-sage-800 underline underline-offset-2"
            >
              {`Open ${pronounForms(duplicate.pronouns as ClientPronouns | null).possessive} record`}
            </Link>
            {duplicate.is_active ? ' or keep going to add another.' : '. Restore the record first to use it, or keep going to add another.'}
            {duplicate.is_active && workItemId && (
              <span className="mt-1.5 block">
                <Button variant="secondary" size="sm" data-testid="cs-use-client" onClick={() => setLinking(duplicate.id)}>
                  Use this client
                </Button>
              </span>
            )}
          </p>
        )}
        <label className="block text-sm text-ink mb-2">
          Preferred name <span className="text-ink-soft">(optional)</span>
          <TextInput nameCase="person" maxLength={100} value={preferred} onChange={(e) => setPreferred(e.target.value)} data-testid="cs-preferred-name"
            className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
        </label>
        <label className="block text-sm text-ink mb-2">
          Service
          <SelectField
            size="field"
            value={engagement}
            onChange={(e) => {
              setServiceGap(false);
              setEngagement(e.target.value as ClientEngagementValue | '');
            }}
            data-testid="cs-service"
            aria-invalid={serviceGap ? true : undefined}
            className="w-full mt-1"
          >
            <option value="">Choose a service</option>
            {(Object.keys(ENGAGEMENT_LABELS) as ClientEngagementValue[]).map((v) => (
              <option key={v} value={v}>
                {ENGAGEMENT_LABELS[v]}
              </option>
            ))}
          </SelectField>
          {serviceGap && (
            <span role="alert" className="mt-1 block text-xs text-pepper" data-testid="cs-service-error">
              Choose a service.
            </span>
          )}
        </label>
        <label className="block text-sm text-ink mb-2">
          Pronouns <span className="text-ink-soft">(optional)</span>
          <SelectField size="field" value={pronouns} onChange={(e) => setPronouns(e.target.value)} data-testid="cs-pronouns"
            className="w-full mt-1">
            <option value="">Not recorded</option>
            {CLIENT_PRONOUN_VALUES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </SelectField>
        </label>
        {offerRateSets && (
          <label className="block text-sm text-ink mb-2">
            Rate set <span className="text-ink-soft">(optional)</span>
            <SelectField size="field" value={rateSetId} onChange={(e) => setRateSetId(e.target.value)} data-testid="cs-rate-set"
              className="w-full mt-1">
              <option value="">No rate set</option>
              {rateSets.map((set) => (
                <option key={set.id} value={set.id}>{set.name}</option>
              ))}
            </SelectField>
          </label>
        )}
        <label className="block text-sm text-ink mb-4">
          CareTree Patient Id <span className="text-ink-soft">(optional)</span>
          <input value={caretreeId} onChange={(e) => setCaretreeId(e.target.value)} data-testid="cs-caretree-id"
            className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
        </label>
        <DialogButtons onCancel={onCancel} submitting={submitting} confirmLabel="Move to Client Setup" canSubmit />
      </form>
    </Dialog>
  );
}

interface TypedValues {
  engagement: ClientEngagementValue | '';
  pronouns: string;
  preferred_name: string;
  caretree_external_id: string;
}

const DIFFERENCE_LABELS: Record<keyof TypedValues, string> = {
  engagement: 'Service',
  pronouns: 'Pronouns',
  preferred_name: 'Preferred name',
  caretree_external_id: 'CareTree Patient Id',
};

/**
 * The confirm step for "Use this client". The duplicate match is first and
 * last name only and a link has no undo, so the step names the client fully
 * (the audited candidate read): date of birth, home town and ZIP, service,
 * preferred name, CareTree id and lead care manager. The card's ZIP is
 * compared with the client's home ZIP and marked when they differ; the card
 * holds no date of birth, so that line is shown unmarked. Values typed in
 * the form that differ from the client's are listed for the record's own
 * doors afterwards: a link writes none of them.
 */
function LinkConfirmStep({
  workItemId,
  clientId,
  cardZip,
  typed,
  submitting,
  onBack,
  onLink,
}: {
  workItemId: string;
  clientId: string;
  cardZip: string | null;
  typed: TypedValues;
  submitting?: boolean;
  onBack: () => void;
  onLink: () => void;
}): ReactNode {
  const { tenantSlug } = useTenant();
  const contact = useSupportContact();
  const { data: c, isError, error } = useLinkCandidate(tenantSlug, workItemId, clientId);
  const zip = cardZip?.trim() || null;
  const homeZip = c?.home?.zip?.trim() || null;
  const zipDiffers = zip !== null && homeZip !== null && zip !== homeZip;
  const differences = c
    ? (Object.keys(DIFFERENCE_LABELS) as Array<keyof TypedValues>).filter((k) => {
        const t = typed[k];
        if (t === '') return false;
        const theirs = k === 'engagement' ? c.engagement : k === 'pronouns' ? c.pronouns : k === 'preferred_name' ? c.preferred_name : c.caretree_external_id;
        return t !== (theirs ?? '');
      })
    : [];
  const shownTyped = (k: keyof TypedValues): string =>
    k === 'engagement' ? ENGAGEMENT_LABELS[typed.engagement as ClientEngagementValue] ?? typed.engagement : typed[k];
  const shownTheirs = (k: keyof TypedValues): string => {
    if (!c) return '';
    if (k === 'engagement') return ENGAGEMENT_LABELS[c.engagement] ?? c.engagement;
    const v = k === 'pronouns' ? c.pronouns : k === 'preferred_name' ? c.preferred_name : c.caretree_external_id;
    return v ?? 'Not recorded';
  };

  return (
    <div data-testid="cs-link-confirm" className="space-y-3 text-sm text-ink">
      <p className="text-xs text-ink-soft">
        This card will become this client&rsquo;s intake card. No new client is made, and nothing on the client changes.
        There is no undo, so check it is the same person.
      </p>
      {isError ? (
        <p role="alert" className="text-sm text-pepper">
          {pageReadText(error, contact, 'this client')}
        </p>
      ) : !c ? (
        <p className="text-ink-soft">Loading the client…</p>
      ) : (
        <>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-ink-soft">Name</dt>
            <dd className="font-medium">
              {c.display_name}
              {c.preferred_name ? ` (${c.preferred_name})` : ''}
            </dd>
            <dt className="text-ink-soft">Status</dt>
            <dd>Active</dd>
            <dt className="text-ink-soft">Service</dt>
            <dd>{ENGAGEMENT_LABELS[c.engagement] ?? c.engagement}</dd>
            <dt className="text-ink-soft">Date of birth</dt>
            <dd data-testid="cs-link-dob">
              {c.dob ? proseDate(c.dob) : 'Not recorded'}
              <span className="text-ink-soft"> · This card: not recorded</span>
            </dd>
            <dt className="text-ink-soft">Home</dt>
            <dd data-testid={zipDiffers ? 'cs-difference-zip' : 'cs-link-home'} className={zipDiffers ? 'font-medium text-pepper-deep' : undefined}>
              {c.home ? [c.home.city, c.home.zip].filter(Boolean).join(' ') || 'Not recorded' : 'Not recorded'}
              {zip !== null && <span className={zipDiffers ? undefined : 'text-ink-soft'}>{` · This card: ZIP ${zip}`}</span>}
              {zipDiffers && <span className="block text-xs">The ZIPs differ.</span>}
            </dd>
            <dt className="text-ink-soft">CareTree Patient Id</dt>
            <dd>{c.caretree_external_id ?? 'None'}</dd>
            <dt className="text-ink-soft">Care manager</dt>
            <dd>{c.primary_cm_name ?? 'Unassigned'}</dd>
          </dl>
          {c.intake_card_count > 0 && (
            <p role="status" data-testid="cs-already-linked" className="rounded-base bg-gold-soft px-2 py-1.5 text-xs text-ink">
              {`${c.display_name} is already the client on another intake card. Linking makes this a second card for the same client.`}
            </p>
          )}
          {differences.length > 0 && (
            <div data-testid="cs-differences">
              <p className="text-xs text-ink-soft">What you typed differs from the client&rsquo;s record. Linking keeps the client&rsquo;s; change it on the record if you need to.</p>
              <ul className="mt-1 space-y-0.5 text-xs">
                {differences.map((k) => (
                  <li key={k} data-testid={`cs-difference-${k}`}>
                    <span className="font-medium">{DIFFERENCE_LABELS[k]}:</span>
                    {` This card: ${shownTyped(k)} · The client: ${shownTheirs(k)}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" data-testid="cs-link-back" onClick={onBack}>
          Back
        </Button>
        {c && !isError && (
          <Button variant="primary" size="sm" data-testid="cs-link-submit" loading={submitting} onClick={onLink}>
            Link this card
          </Button>
        )}
      </div>
    </div>
  );
}

function AssignForm({ onConfirm, onCancel, submitting, unitId, prospectZip, clientId, initialRosterId, offerSpice = false, spiceFirst = false }: MoveStageDialogProps): ReactNode {
  const { tenantSlug } = useTenant();
  const { careManagers, spiceTherapists, units, isLoading, isError, error: optionsError } = useAssignOptions(tenantSlug);
  // LP-3: the SPICE therapist, beside or instead of the care manager.
  const [spiceId, setSpiceId] = useState<string>('');
  const contact = useSupportContact();
  const zip = prospectZip?.trim() ?? '';
  const suggestFor = clientId ?? null;
  const [rosterId, setRosterId] = useState<string>(initialRosterId ?? '');
  const [unitIds, setUnitIds] = useState<Set<string>>(new Set());
  // Slice 3b: default the CM list to the work_item's unit (CMs carry unit_id), with
  // a "show all units" widen. Soft only — a deliberate cross-unit pick is allowed.
  const [showAllCms, setShowAllCms] = useState<boolean>(!!initialRosterId && !!unitId);
  const unitScoped = !!unitId && !showAllCms;
  const visibleCms = unitScoped ? careManagers.filter((cm) => cm.unit_id === unitId) : careManagers;

  const toggleUnit = (id: string): void =>
    setUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Mirror the server STOP: neither lead nor any unit chosen.
  const canSubmit = rosterId !== '' || (offerSpice && spiceId !== '') || unitIds.size > 0;

  // A suggested name fills the picker; when the pick sits outside the
  // card's unit the unit-scoped list is widened so the picker can show it.
  const pickSuggested = (row: SuggestedCareManagerRow): void => {
    if (unitScoped && row.unit_id !== unitId) setShowAllCms(true);
    setRosterId(row.roster_id);
  };

  // "Assign care manager" = assignee_roster_id → clients.primary_cm_roster_id (who
  // manages the client's care; set once, terminal). NEVER label this bare "Assign"
  // — that collides with the per-stage "hand off / assign to" desk owner
  // (assignee_user_id), a distinct control (slice 5).
  return (
    <Dialog title={offerSpice && spiceFirst ? 'Assign SPICE therapist' : 'Assign care manager'} testId="move-stage-dialog" onClose={onCancel}>
      {isLoading ? (
        <p className="text-sm text-ink-soft py-4">Loading options…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-pepper py-4" data-testid="assign-options-error">
          {pageReadText(optionsError, contact, 'the care managers and units')}
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            onConfirm({
              assignee_roster_id: rosterId || undefined,
              ...(offerSpice && spiceId ? { spice_therapist_roster_id: spiceId } : {}),
              unit_ids: [...unitIds],
            });
          }}
        >
          {(suggestFor !== null || zip !== '') && (
            <div className="mb-4 border-b border-sage-800/[8%] pb-3">
              <p className="text-label font-semibold uppercase tracking-[0.12em] text-sage-400 mb-1">Suggested</p>
              <SuggestedCareManagers
                zip={zip}
                clientId={suggestFor}
                selectedId={rosterId || null}
                onSelect={pickSuggested}
                testIdPrefix="assign-suggested"
              />
            </div>
          )}
          <label className="block text-sm text-ink mb-3">
            Care manager <span className="text-ink-soft">(optional)</span>
            <SelectField size="field" value={rosterId} onChange={(e) => setRosterId(e.target.value)} data-testid="assign-cm-select"
              className="w-full mt-1">
              <option value="">None</option>
              {visibleCms.map((cm) => (
                <option key={cm.roster_id} value={cm.roster_id}>{cm.name}</option>
              ))}
            </SelectField>
            {!!unitId && (
              <TextAction data-testid="assign-cm-widen" onClick={() => setShowAllCms((v) => !v)} className="mt-1">
                {showAllCms ? 'Show this unit’s care managers' : 'Show all units’ care managers'}
              </TextAction>
            )}
          </label>
          {/* Slice 3c: who am I picking? — photo + bio snippet for the chosen CM. */}
          {rosterId !== '' &&
            (() => {
              const sel = careManagers.find((cm) => cm.roster_id === rosterId);
              if (!sel) return null;
              const snippet = sel.bio
                ? sel.bio.length > 90
                  ? `${sel.bio.slice(0, 90).trimEnd()}…`
                  : sel.bio
                : null;
              return (
                <div className="mb-3 flex items-start gap-2.5 p-2 rounded-base bg-sand/60" data-testid="assign-cm-preview">
                  <CmPhoto tenantSlug={tenantSlug} rosterId={sel.roster_id} name={sel.name} size="md" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-sage-800">{sel.name}</p>
                    {snippet ? (
                      <p className="text-xs text-ink-soft">{snippet}</p>
                    ) : (
                      <p className="text-xs text-ink-soft italic">No bio yet.</p>
                    )}
                  </div>
                </div>
              );
            })()}
          {offerSpice && (
            <label className="block text-sm text-ink mb-3">
              SPICE therapist <span className="text-ink-soft">(optional)</span>
              <SelectField size="field" value={spiceId} onChange={(e) => setSpiceId(e.target.value)} data-testid="assign-spice-select"
                className="w-full mt-1">
                <option value="">None</option>
                {spiceTherapists.map((t) => (
                  <option key={t.roster_id} value={t.roster_id}>{t.name}</option>
                ))}
              </SelectField>
            </label>
          )}
          <fieldset className="mb-4">
            <legend className="text-sm text-ink mb-1">Units</legend>
            <div className="space-y-1 max-h-40 overflow-auto">
              {units.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={unitIds.has(u.id)} onChange={() => toggleUnit(u.id)}
                    data-testid={`assign-unit-${u.id}`} />
                  {u.name}
                </label>
              ))}
            </div>
          </fieldset>
          {!canSubmit && (
            <p className="text-xs text-pepper mb-3" data-testid="assign-stop-hint">
              {offerSpice ? 'Pick a care manager, a SPICE therapist, or at least one unit.' : 'Pick a care manager or at least one unit.'}
            </p>
          )}
          <DialogButtons onCancel={onCancel} submitting={submitting} confirmLabel="Assign" canSubmit={canSubmit} />
        </form>
      )}
    </Dialog>
  );
}

function DialogButtons({
  onCancel,
  submitting,
  confirmLabel,
  canSubmit,
}: {
  onCancel: () => void;
  submitting?: boolean;
  confirmLabel: string;
  canSubmit: boolean;
}): ReactNode {
  return (
    <div className="flex justify-end gap-2">
      <TextAction onClick={onCancel}>Cancel</TextAction>
      <Button size="sm" type="submit" disabled={submitting || !canSubmit} data-testid="move-stage-confirm">
        {submitting ? 'Working…' : confirmLabel}
      </Button>
    </div>
  );
}
